import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

import {
  createDonationApi,
  getDonationCampaignsApi,
  getDonationDetailsByPostApi,
  getDonationTransactionsApi,
} from "../../api/DonationActions";

interface DonationState {
  campaigns: any[];
  transactions: any[];
  postDonations: any[];

  loading: boolean;
  postDonationLoading: boolean;
  submitLoading: boolean;

  error: string | null;
}

interface PostDonationPayload {
  post_id: number | string;
  client_id: number | string;
}

const initialState: DonationState = {
  campaigns: [],
  transactions: [],
  postDonations: [],

  loading: false,
  postDonationLoading: false,
  submitLoading: false,

  error: null,
};

// ==========================
// RESPONSE ARRAY HELPER
// Handles different API shapes
// ==========================
const getResponseRows = (response: any): any[] => {
  if (Array.isArray(response?.data)) {
    return response.data;
  }

  if (Array.isArray(response?.data?.transactions)) {
    return response.data.transactions;
  }

  if (Array.isArray(response?.data?.rows)) {
    return response.data.rows;
  }

  if (Array.isArray(response?.data?.data)) {
    return response.data.data;
  }

  return [];
};

// ==========================
// GET DONATION CAMPAIGNS
// ==========================
export const fetchDonationCampaigns = createAsyncThunk<
  any[],
  number | string,
  { rejectValue: string }
>(
  "donation/fetchDonationCampaigns",
  async (client_id, { rejectWithValue }) => {
    try {
      const res = await getDonationCampaignsApi(client_id);

      if (res?.status === false) {
        return rejectWithValue(
          res?.message || "Failed to fetch donation campaigns"
        );
      }

      return getResponseRows(res);
    } catch (error: any) {
      return rejectWithValue(
        error?.response?.data?.message ||
          "Failed to fetch donation campaigns"
      );
    }
  }
);

// ==========================
// GET DONATION TRANSACTIONS
// Uses billing-finance transactions
// ==========================
export const fetchDonationTransactions = createAsyncThunk<
  any[],
  {
    client_id: number | string;
    contact_id?: number | string | null;
    user_id?: number | string | null;
    page?: number;
    limit?: number;
  },
  { rejectValue: string }
>(
  "donation/fetchDonationTransactions",
  async (payload, { rejectWithValue }) => {
    try {
      const res = await getDonationTransactionsApi(payload);

      if (res?.status === false) {
        return rejectWithValue(
          res?.message || "Failed to fetch donation transactions"
        );
      }

      return getResponseRows(res);
    } catch (error: any) {
      return rejectWithValue(
        error?.response?.data?.message ||
          "Failed to fetch donation transactions"
      );
    }
  }
);

// ==========================
// GET DONATIONS BY POST
// Uses centralized transactions
// ==========================
export const fetchDonationDetailsByPost = createAsyncThunk<
  any[],
  PostDonationPayload,
  { rejectValue: string }
>(
  "donation/fetchDonationDetailsByPost",
  async (payload, { rejectWithValue }) => {
    try {
      const res = await getDonationDetailsByPostApi(
        payload.post_id,
        payload.client_id
      );

      if (res?.status === false) {
        return rejectWithValue(
          res?.message || "Failed to fetch post donations"
        );
      }

      return getResponseRows(res);
    } catch (error: any) {
      return rejectWithValue(
        error?.response?.data?.message ||
          "Failed to fetch post donations"
      );
    }
  }
);

// ==========================
// CREATE DONATION
// Creates payment order/session
// ==========================
export const createDonation = createAsyncThunk<
  any,
  any,
  { rejectValue: string }
>(
  "donation/createDonation",
  async (payload, { rejectWithValue }) => {
    try {
      const res: any =
        await createDonationApi(payload);

      if (res?.status !== true) {
        return rejectWithValue(
          res?.message ||
            "Unable to start donation payment"
        );
      }

      const responseData =
        res?.data || {};

      return {
        status: true,

        message:
          res?.message ||
          "Payment session created successfully",

        data: {
          ...responseData,

          order_id:
            responseData.order_id || null,

          payment_session_id:
            responseData.payment_session_id ||
            null,

          cf_order_id:
            responseData.cf_order_id || null,

          gateway:
            responseData.gateway || null,

          client_id:
            payload.client_id,

          post_id:
            payload.post_id,

          user_id:
            payload.user_id || null,

          contact_id:
            payload.contact_id || null,

          donor_name:
            payload.donor_name,

          mobile:
            payload.mobile,

          email:
            payload.email,

          amount:
            payload.amount,

          payment_method_id:
            payload.payment_method_id,

          donation_source:
            payload.donation_source,

          visibility:
            payload.visibility,

          reference_type:
            payload.reference_type,

          reference_id:
            payload.reference_id,

          payment_status:
            responseData.payment_status ||
            responseData.order_status ||
            "initiated",
        },
      };
    } catch (error: any) {
      return rejectWithValue(
        error?.response?.data?.message ||
          error?.message ||
          "Unable to start donation payment"
      );
    }
  }
);

const donationSlice = createSlice({
  name: "donation",

  initialState,

  reducers: {
    clearDonationState: (state) => {
      state.error = null;
    },

    clearDonationData: (state) => {
      state.campaigns = [];
      state.transactions = [];
      state.postDonations = [];
      state.error = null;
    },

    clearPostDonations: (state) => {
      state.postDonations = [];
      state.error = null;
    },
  },

  extraReducers: (builder) => {
    builder

      // ==========================
      // CAMPAIGNS
      // ==========================
      .addCase(
        fetchDonationCampaigns.pending,
        (state) => {
          state.loading = true;
          state.error = null;
        }
      )

      .addCase(
        fetchDonationCampaigns.fulfilled,
        (state, action) => {
          state.loading = false;
          state.campaigns = action.payload;
        }
      )

      .addCase(
        fetchDonationCampaigns.rejected,
        (state, action) => {
          state.loading = false;
          state.error =
            action.payload ||
            "Failed to fetch donation campaigns";
        }
      )

      // ==========================
      // TRANSACTIONS
      // ==========================
      .addCase(
        fetchDonationTransactions.pending,
        (state) => {
          state.loading = true;
          state.error = null;
        }
      )

      .addCase(
        fetchDonationTransactions.fulfilled,
        (state, action) => {
          state.loading = false;
          state.transactions = action.payload;
        }
      )

      .addCase(
        fetchDonationTransactions.rejected,
        (state, action) => {
          state.loading = false;
          state.error =
            action.payload ||
            "Failed to fetch donation transactions";
        }
      )

      // ==========================
      // DONATIONS BY POST
      // ==========================
      .addCase(
        fetchDonationDetailsByPost.pending,
        (state) => {
          state.postDonationLoading = true;
          state.error = null;
        }
      )

      .addCase(
        fetchDonationDetailsByPost.fulfilled,
        (state, action) => {
          state.postDonationLoading = false;
          state.postDonations = action.payload;
        }
      )

      .addCase(
        fetchDonationDetailsByPost.rejected,
        (state, action) => {
          state.postDonationLoading = false;
          state.error =
            action.payload ||
            "Failed to fetch post donations";
        }
      )

      // ==========================
      // CREATE DONATION
      // ==========================
      .addCase(
        createDonation.pending,
        (state) => {
          state.submitLoading = true;
          state.error = null;
        }
      )

      .addCase(
        createDonation.fulfilled,
        (state, action) => {
          state.submitLoading = false;

          const donation = action.payload?.data;

          if (!donation) {
            return;
          }

          /*
           * Add immediately only when the backend
           * confirms successful payment.
           *
           * Online Cashfree donations may initially
           * return initiated/pending status.
           */
          if (donation.payment_status === "success") {
            state.transactions.unshift(donation);

            if (donation.reference_type === "post") {
              state.postDonations.unshift(donation);
            }
          }
        }
      )

      .addCase(
        createDonation.rejected,
        (state, action) => {
          state.submitLoading = false;
          state.error =
            action.payload ||
            "Donation failed";
        }
      );
  },
});

export const {
  clearDonationState,
  clearDonationData,
  clearPostDonations,
} = donationSlice.actions;

export default donationSlice.reducer;